import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { refreshToken } from '../../../store/auth/actions';
import { useSelector } from 'react-redux';
import { Refresh } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import Menu from '@mui/material/Menu';
import Loading from '../../common/loading';
import MessageError from '../../common/message-error';
import Table from '../../common/table';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import TopDashboardSection from '../../common/top-dashboard-section';
import ButtonMoreMenu from '../../common/button-more-menu';
import { Button } from '../../common/button';
import DialogConfirm from '../../common/confirm/dialog-confirm';
import CardConfirm from '../../common/confirm/card-confirm';
import InputSearch from '../../common/input-search';
import { ReactComponent as EmptyIcon } from '../../../assets/icons/empty-subscriptions.svg';
import { ReactComponent as HeaderUserManagementIcon } from '../../../assets/icons/user-management-header.svg';
import { ReactComponent as InviteIcon } from '../../../assets/icons/user-management-invite.svg';
import { ReactComponent as RoleOwnerIcon } from '../../../assets/icons/role-owner.svg';
import { ReactComponent as RoleAdministratorIcon } from '../../../assets/icons/role-administrator.svg';
import { ReactComponent as RoleFinanceIcon } from '../../../assets/icons/role-finance.svg';
import { ReactComponent as RoleSalesCSIcon } from '../../../assets/icons/role-sales-cs.svg';
import { ReactComponent as RoleTechnicalIcon } from '../../../assets/icons/role-technical.svg';
import { ReactComponent as RoleReadOnlyIcon } from '../../../assets/icons/role-read-only.svg';
import { ReactComponent as ChangeRoleIcon } from '../../../assets/icons/change-role.svg';
import { ReactComponent as ChangeOwnerIcon } from '../../../assets/icons/change-owner.svg';
import { ReactComponent as RemoveUserIcon } from '../../../assets/icons/remove-user.svg';
import l from '../../../lang';
import { useUserManagement } from '../../../store/user-management';
import { MerchantUser } from '../../../store/user-management/interfaces';
import { AuthSelectors } from '../../../store/auth/selectors';
import { canManageUsers } from '../../../store/user-management/roles';
import { useDashboard } from '../../../store/dashboard';
import { Urls } from '../../../config/urls';
import { patchTask } from '../../../services';
import { ReactComponent as CheckTickIcon } from '../../../assets/icons/check-tick-blue.svg';
import DialogDeleteUser from './dialog-delete';
import DialogInvite from './dialog-invite';
import './styles.scss';

const PAGE_SIZE = 10;

const UserManagement = () => {
  const [tableBlockingLoading, setTableBlockingLoading] = useState(false);
  const dispatch = useDispatch();
  const {
    loading,
    error,
    errorInfo,
    allMerchantUsers,
    deleteSuccess,
    swapOwnerSuccess,
    getMerchantUsers,
    deleteMerchantUser,
    updateUserRole,
    swapOwnerMerchantUser,
    cleanError,
  } = useUserManagement();
  const { merchantMe, userMe, getMerchantMe } = useDashboard();
  const [deleteUser, setDeleteUser] = useState<MerchantUser | null>(null);
  const [deletedUserEmail, setDeletedUserEmail] = useState<string | null>(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const handleInviteSuccess = () => {
    const pageSize = allMerchantUsers?.size || PAGE_SIZE;
    getMerchantUsers(0, pageSize, currentSearchFilters);
  };
  const [changeOwnerUser, setChangeOwnerUser] = useState<MerchantUser | null>(null);
  const [swappedOwnerEmail, setSwappedOwnerEmail] = useState<string | null>(null);
  const [showSwapOwnerSuccess, setShowSwapOwnerSuccess] = useState(false);
  const roles = useSelector(AuthSelectors.getRoles);
  const token = useSelector((state: any) => state.auth.token);
  const canManage = canManageUsers(roles);
  const isOwner = roles.includes('ROLE_OWNER');
  const isAdmin = roles.includes('ROLE_ADMINISTRATOR');
  const [currentSearchFilters, setCurrentSearchFilters] = useState<{ email?: string; name?: string }>({});
  useEffect(() => {
    if (!canManage) {
      setRoleMenuUser(null);
      setRoleMenuAnchorEl(null);
      setSelectedRole(null);
      setRoleAssignedMsg(null);
    }
  }, [canManage]);

  useEffect(() => {
    if (canManage) {
      getMerchantUsers(0, PAGE_SIZE);
    }
  }, []);

  useEffect(() => {
    if (allMerchantUsers?.outdated) {
      const pageSize = allMerchantUsers.size || PAGE_SIZE;
      getMerchantUsers(0, pageSize);
    }
  }, [allMerchantUsers?.outdated]);

  useEffect(() => {
    if (deleteSuccess) {
      setShowDeleteSuccess(true);
      setDeletedUserEmail(deleteUser?.email || null);
      setDeleteUser(null);
      const pageSize = allMerchantUsers?.size || PAGE_SIZE;
      getMerchantUsers(0, pageSize, currentSearchFilters);
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (swapOwnerSuccess) {
      setShowSwapOwnerSuccess(true);
      setSwappedOwnerEmail(changeOwnerUser?.email || null);
      setChangeOwnerUser(null);
      getMerchantMe();
      const pageSize = allMerchantUsers?.size || PAGE_SIZE;
      getMerchantUsers(0, pageSize, currentSearchFilters);
    }
  }, [swapOwnerSuccess]);

  const handleCloseSuccess = () => {
    setShowDeleteSuccess(false);
    setDeletedUserEmail(null);
    cleanError();
  };

  const handleCloseSwapOwnerSuccess = () => {
    setShowSwapOwnerSuccess(false);
    setSwappedOwnerEmail(null);
    cleanError();
  };

  const dataExist = allMerchantUsers && allMerchantUsers?.data?.length > 0;

  const handleDeleteUser = (user: MerchantUser) => {
    setDeleteUser(user);
  };

  const handleConfirmDelete = (confirm: boolean) => {
    if (confirm && deleteUser) {
      deleteMerchantUser(deleteUser.id);
    } else {
      setDeleteUser(null);
    }
  };

  const handleChangeOwner = (user: MerchantUser) => {
    setChangeOwnerUser(user);
  };

  const handleConfirmChangeOwner = (confirm: boolean) => {
    if (confirm && changeOwnerUser) {
      swapOwnerMerchantUser(changeOwnerUser.id);
    } else {
      setChangeOwnerUser(null);
    }
  };

  // Change role popup state
  const [roleMenuUser, setRoleMenuUser] = useState<MerchantUser | null>(null);
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [roleAssignedMsg, setRoleAssignedMsg] = useState<string | null>(null);

  const openRoleMenu = (user: MerchantUser) => {
    const anchor = document.getElementById(`user-actions-${user.id}`);
    setRoleMenuAnchorEl(anchor as HTMLElement);
    setRoleMenuUser(user);
    setSelectedRole(user.roles?.[0] || null);
    setRoleAssignedMsg(null);
  };

  const [shouldRefreshToken, setShouldRefreshToken] = useState(false);

  const closeRoleMenu = async () => {
    setRoleMenuUser(null);
    setRoleMenuAnchorEl(null);
    setSelectedRole(null);
    setRoleAssignedMsg(null);
    const pageSize = allMerchantUsers?.size || PAGE_SIZE;
    const page = allMerchantUsers?.page || 0;
    getMerchantUsers(page, pageSize, currentSearchFilters);
    if (shouldRefreshToken) {
      setShouldRefreshToken(false);
      const refreshResult = await fetch(Urls.api.refreshLogin, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (refreshResult.ok) {
        const data = await refreshResult.json();
        dispatch(refreshToken(data));
      }
    }
  };

  const handleSelectRole = async (role: string) => {
    if (!roleMenuUser) return;
    if (role === 'ROLE_OWNER') return;
    setRoleMenuUser(null);
    setRoleMenuAnchorEl(null);
    setSelectedRole(null);
    setRoleAssignedMsg(null);
    setTableBlockingLoading(true);

    try {
      const result = await patchTask({
        url: `${Urls.api.deleteMerchantUser.replace(':merchantUserId', String(roleMenuUser.id))}/role/${role}`,
        token,
        body: {},
      });
      if ((result as any).response) {
        setSelectedRole(role);
        setRoleAssignedMsg(`Rol asignado: ${l(`roles.${role}`)}`);

        updateUserRole(roleMenuUser.id, role);
        if (userMe?.id === roleMenuUser.id) {
          const refreshResult = await fetch(Urls.api.refreshLogin, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({}),
          });
          if (refreshResult.ok) {
            const data = await refreshResult.json();
            dispatch(refreshToken(data));
          }
        }
      } else {
        setRoleAssignedMsg('Error al asignar rol');
      }
    } catch (e) {
      setRoleAssignedMsg('Error al asignar rol');
    } finally {
      setTableBlockingLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ROLE_OWNER':
        return <RoleOwnerIcon className="user-management__role_icon" />;
      case 'ROLE_ADMINISTRATOR':
        return <RoleAdministratorIcon className="user-management__role_icon" />;
      case 'ROLE_FINANCE':
        return <RoleFinanceIcon className="user-management__role_icon" />;
      case 'ROLE_SALES_CS':
        return <RoleSalesCSIcon className="user-management__role_icon" />;
      case 'ROLE_TECHNICAL':
        return <RoleTechnicalIcon className="user-management__role_icon" />;
      case 'ROLE_READ_ONLY':
        return <RoleReadOnlyIcon className="user-management__role_icon" />;
      default:
        return null;
    }
  };

  const ActiveStatusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="6" fill="none" />
      <circle cx="12" cy="12" r="6.5" fill="#4CAF50" />
      <path d="M14.25 9.75L11 13l-1.75-1.75" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const PendingStatusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="6" fill="none" />
      <circle cx="12" cy="12" r="6.5" fill="#D5A600" />
      <path d="M12 8.5v4h2" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="5" stroke="white" strokeWidth="0.5" fill="none" />
    </svg>
  );

  const ChangeOwnerIcon2 = () => (
    <svg width="89" height="89" viewBox="0 0 89 89" fill="none">
      <circle cx="44.5" cy="44.5" r="44.5" fill="#CFE3FF" />
      <g clipPath="url(#clip0_2312_10448)">
        <path
          d="M39.9273 46.5668L44.3101 50.7031L48.6199 46.5668M50.9939 48.1647C49.2225 46.5394 46.885 45.5532 44.3192 45.5532M44.3192 45.5532C38.7768 45.5532 34.2388 50.1643 34.2388 55.7889M44.3192 45.5532C48.7112 45.5532 52.327 41.8826 52.327 37.4268C52.327 32.9709 48.7842 29.3003 44.3192 29.3003C39.8543 29.3003 36.3115 32.98 36.3115 37.4268C36.3115 41.9557 39.9273 45.5532 44.3192 45.5532Z"
          stroke="#3272F0"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M56.8563 54.2002H48.6294" stroke="#006CFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M52.4187 58.7111L56.8563 54.2004L52.4187 49.772"
          stroke="#006CFA"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_2312_10448">
          <rect width="49.4078" height="37.1504" fill="white" transform="translate(21.7961 28.2788)" />
        </clipPath>
      </defs>
    </svg>
  );

  const getStatusChip = (enabled: boolean) => {
    if (enabled) {
      return (
        <span className="user-management__status_chip user-management__status_chip--active">
          <ActiveStatusIcon />
          <span>{l('userManagement.statusActive')}</span>
        </span>
      );
    }

    return (
      <span className="user-management__status_chip user-management__status_chip--pending">
        <PendingStatusIcon />
        <span>{l('userManagement.statusPending')}</span>
      </span>
    );
  };

  const canRemoveUser = (user: MerchantUser): boolean => {
    const isSelf = userMe?.id === user.id;
    const isTargetOwner = user.roles && user.roles.includes('ROLE_OWNER');

    if (isOwner) {
      // Owner puede eliminar a todos excepto a sí mismo
      return !isSelf;
    }

    if (isAdmin) {
      // Admin puede eliminar a todos excepto a owners
      return !isTargetOwner;
    }

    return false;
  };

  const canChangeRole = (user: MerchantUser): boolean => {
    // No permitir cambiar rol si el usuario está pendiente
    if (!user.enabled) {
      return false;
    }

    const isTargetOwner = user.roles && user.roles.includes('ROLE_OWNER');

    if (isOwner) {
      // Owner puede cambiar rol a todos, incluso a sí mismo
      return true;
    }

    if (isAdmin) {
      // Admin puede cambiar rol a todos excepto a owners
      return !isTargetOwner;
    }

    return false;
  };

  if (!canManage) {
    return (
      <div className="user-management">
        <TopDashboardSection title={l('menu.item.USER_MANAGEMENT')} />
        <Card>
          <CardDescription>{l('userManagement.noPermission')}</CardDescription>
        </Card>
      </div>
    );
  }

  return (
    <div className="user-management">
      <TopDashboardSection title={l('menu.item.USER_MANAGEMENT')} subtitle={l('userManagement.subtitle')} />
      <Card>
        <CardDescription>{l('userManagement.description')}</CardDescription>
        <div className="user-management__content">
          {}
          {tableBlockingLoading && (
            <div className="user-management__loading user-management__loading--blocking">
              <Loading fullScreen />
            </div>
          )}
          {loading && !allMerchantUsers && !tableBlockingLoading && (
            <div className="user-management__loading">
              <Loading />
            </div>
          )}
          {showDeleteSuccess && (
            <DialogConfirm onClose={handleCloseSuccess}>
              <CardConfirm
                onClose={handleCloseSuccess}
                headline={l('userManagement.deleteSuccess')}
                description={l('userManagement.deleteSuccess.message', `${deletedUserEmail}`)}
                confirmChildren={l('common.close')}
                level={'delete-success'}
              />
            </DialogConfirm>
          )}
          {showSwapOwnerSuccess && (
            <DialogConfirm onClose={handleCloseSwapOwnerSuccess}>
              <CardConfirm
                onClose={handleCloseSwapOwnerSuccess}
                headline={l('userManagement.changeOwnerSuccess')}
                description={l('userManagement.changeOwnerSuccess.message', `${swappedOwnerEmail}`)}
                confirmChildren={l('common.close')}
                level={'delete-success'}
              />
            </DialogConfirm>
          )}
          {error && (
            <div className="user-management__error">
              <MessageError iconClose={<Refresh />} errorInfo={errorInfo} onClose={() => getMerchantUsers(0, PAGE_SIZE)} />
            </div>
          )}
          {!error && allMerchantUsers && (
            <div>
              <div className="user-management__header">
                <div className="user-management__header_left">
                  <HeaderUserManagementIcon className="user-management__header_icon" />
                  <div className="user-management__header_info">
                    <div className="user-management__header_title">{l('userManagement.teamMembers')}</div>
                    <div className="user-management__header_subtitle">
                      {l('userManagement.membersCount', allMerchantUsers?.totalElements || 0)}
                    </div>
                  </div>
                </div>
                <Button startIcon={<InviteIcon />} onClick={() => setShowInviteDialog(true)} disabled={!canManage} className="user-management__invite_button">
                  {l('userManagement.invite')}
                </Button>
              </div>
              <div className="user-management__search">
                <InputSearch
                  searchOptions={[
                    { value: 'email', label: 'Email' },
                    { value: 'name', label: 'Apellido, Nombre' },
                  ]}
                  placeholder="Buscar Miembros"
                  isLoading={loading}
                  helpText={''}
                  keepClearButtonWhenEmpty={true}
                  onSearch={(type, value) => {
                    if (value === '') {
                      // Si se limpia la búsqueda, obtener todos los usuarios
                      setCurrentSearchFilters({});
                      getMerchantUsers(0, PAGE_SIZE);
                    } else {
                      // Si hay un valor de búsqueda
                      const newFilters = { [type]: value };
                      setCurrentSearchFilters(newFilters as { email?: string; name?: string });
                      getMerchantUsers(0, PAGE_SIZE, newFilters as { email?: string; name?: string });
                    }
                  }}
                />
              </div>
              <Table
                dense
                withBtnMenu
                headers={[
                  <span className="user-management__column__left">{l('userManagement.member')}</span>,
                  <span className="user-management__column__left">{l('userManagement.email')}</span>,
                  <span className="user-management__column__left">{l('userManagement.role')}</span>,
                  <span className="user-management__column__left">{l('userManagement.status')}</span>,
                  <span className="user-management__column__left">Acciones</span>,
                ]}
                emptyBody={
                  <div className="user-management__no_data">
                    <EmptyIcon />
                    {Object.keys(currentSearchFilters).length > 0 &&
                    (currentSearchFilters.email || currentSearchFilters.name) ? (
                      <>
                        <div className="user-management__no_data__title">No se encontraron resultados</div>
                        <div className="user-management__no_data__description">
                          No hay miembros que coincidan con los filtros de búsqueda aplicados. Intenta con otros criterios.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="user-management__no_data__title">{l('userManagement.emptyUsers.title')}</div>
                        <div className="user-management__no_data__description">{l('userManagement.emptyUsers.body')}</div>
                      </>
                    )}
                  </div>
                }
                body={
                  (dataExist &&
                    allMerchantUsers?.data?.map(user => [
                      <span className="user-management__column__left user-management__member_cell">
                        {user.roles && user.roles.length > 0 && getRoleIcon(user.roles[0])}
                        <span>{user.name}</span>
                      </span>,
                      <span className="user-management__column__left">{user.email}</span>,
                      <span className="user-management__column__left">
                        {user.roles && user.roles.length > 0 ? l(`roles.${user.roles[0]}`) : '-'}
                      </span>,
                      <span className="user-management__column__left">{getStatusChip(user.enabled)}</span>,
                      <span className="user-management__actions_cell">
                        <ButtonMoreMenu
                          id={`user-actions-${user.id}`}
                          title={l('userManagement.actions')}
                          items={[
                            {
                              content: (
                                <span className="user-management__menu_item">
                                  <ChangeRoleIcon />
                                  {l('userManagement.changeRole')}
                                </span>
                              ),
                              onClick: () => openRoleMenu(user),
                              disabled: !canChangeRole(user) || (isOwner && userMe?.id === user.id),
                            },
                            {
                              content: (
                                <span className="user-management__menu_item">
                                  <ChangeOwnerIcon className="user-management__menu_icon--owner" />
                                  {l('userManagement.changeOwner')}
                                </span>
                              ),
                              onClick: () => handleChangeOwner(user),
                              disabled:
                                !isOwner || userMe?.id === user.id || !user.enabled || user.roles?.includes('ROLE_OWNER'),
                            },
                            {
                              content: (
                                <span className="user-management__menu_item user-management__menu_item--remove">
                                  <RemoveUserIcon />
                                  <span className="user-management__alert_label">{l('userManagement.removeUser')}</span>
                                </span>
                              ),
                              onClick: () => handleDeleteUser(user),
                              disabled: !canRemoveUser(user),
                            },
                          ]}
                        />
                      </span>,
                    ])) ||
                  null
                }
                rowsPerPageOptions={[allMerchantUsers?.size || PAGE_SIZE]}
                rowsLength={allMerchantUsers?.totalElements}
                rowsPerPage={allMerchantUsers?.size || PAGE_SIZE}
                page={allMerchantUsers?.page || 0}
                handleChangePage={(event, newPage) =>
                  getMerchantUsers(newPage, allMerchantUsers?.size || PAGE_SIZE, currentSearchFilters)
                }
                maxHeight={undefined}
              />
            </div>
          )}
        </div>
      </Card>
      {deleteUser && <DialogDeleteUser userEmail={deleteUser.email} onClose={handleConfirmDelete} />}
      {changeOwnerUser && (
        <DialogConfirm onClose={() => handleConfirmChangeOwner(false)}>
          <CardConfirm
            onClose={handleConfirmChangeOwner}
            headline={l('userManagement.changeOwner.title')}
            description={l('userManagement.changeOwner.description')}
            confirmChildren={l('userManagement.changeOwner.confirm')}
            cancelChildren={l('userManagement.changeOwner.cancel')}
            level={'change-owner'}
            customIcon={<ChangeOwnerIcon2 />}
          />
        </DialogConfirm>
      )}
      {/* Change Role Menu anchored to More button */}
      <Menu
        id="change-role-menu"
        anchorEl={roleMenuAnchorEl}
        open={Boolean(roleMenuUser)}
        onClose={closeRoleMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ disablePadding: true }}
      >
        {roleMenuUser && (
          <div style={{ minWidth: 240, padding: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px' }}>
              <div style={{ fontWeight: 600 }}>{l('userManagement.changeRole')}</div>
              <span onClick={closeRoleMenu} style={{ cursor: 'pointer', display: 'inline-flex' }}>
                <Close fontSize="small" />
              </span>
            </div>
            <div style={{ padding: '4px 0' }}>
              {['ROLE_OWNER', 'ROLE_ADMINISTRATOR', 'ROLE_FINANCE', 'ROLE_SALES_CS', 'ROLE_TECHNICAL', 'ROLE_READ_ONLY'].map(
                r => {
                  const disabled = r === 'ROLE_OWNER';
                  const isSelected = selectedRole === r;
                  return (
                    <div
                      key={r}
                      onClick={() => !disabled && handleSelectRole(r)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.5 : 1,
                      }}
                    >
                      <span style={{ width: 19, display: 'inline-flex', justifyContent: 'center' }}>
                        {isSelected && <CheckTickIcon style={{ width: 19, height: 14 }} />}
                      </span>
                      <span style={{ marginLeft: 8 }}>{l(`roles.${r}`)}</span>
                    </div>
                  );
                }
              )}
            </div>
            {roleAssignedMsg && <div style={{ padding: '6px 8px', color: '#1976d2', fontSize: 12 }}>{roleAssignedMsg}</div>}
          </div>
        )}
      </Menu>
      <DialogInvite open={showInviteDialog} onClose={() => setShowInviteDialog(false)} />
      <DialogInvite
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSuccessInvite={handleInviteSuccess}
      />
    </div>
  );
};

export default UserManagement;
